import ScheduledMessageEditorWrapper, { ScheduledMessageEditorState } from '@extension/context/content/components/add-scheduled-message-button/scheduled-message-editor-wrapper';
import { useEffect, useRef, useState } from 'react';
import { injectShadowDOMTextSelection } from './shadow-dom-text-selection';
import { DirectionToggle } from './direction-toggle';

import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { addScheduledMessageButtonActions } from '@common/reducers/AddScheduledMessageButtonReducer';
import { MessageState } from '@common/types/interface';
import ScheduledMessageEditorPopup from '@extension/context/content/components/add-scheduled-message-button/scheduled-message-editor-popup';
import { ContactTable } from '@extension/context/content/components/bulk-dashboard/contact-table/contact-table';
import { WelcomePopup } from '@common-components/welcome-popup';
import SubscriptionStatusBar from '@common-components/subscription-status-bar';
import { SubscriptionObject } from '../../../bt-common/services/subscription-object.class';

// Structural stand-ins for SubscriptionObject — only the entitlement surface the
// plan badge reads. The badge derives the tier from these, exactly as in prod.
const fakeSubscription = (overrides: Record<string, unknown> = {}) =>
  ({
    isActive: true,
    seatCount: 1,
    messageLimit: Infinity,
    hasBulk: false,
    hasTasks: () => false,
    hasSocket: () => false,
    ...overrides,
  }) as unknown as SubscriptionObject;

// Dev-only showcase copy — never shown to customers, so it is not translated.
const PLAN_SECTION = {
  title: 'Plan badges',
  description: 'The header plan chip for each tier, driven by mock entitlements. Hover a chip for its card.',
};

const PLAN_PREVIEWS:{ label: string; note: string; subscription: SubscriptionObject | null }[] = [
  { label: 'Free', note: 'no subscription', subscription: null },
  { label: 'Free (limited sub)', note: 'messageLimit 5', subscription: fakeSubscription({ messageLimit: 5 }) },
  { label: 'Basic', note: 'unlimited only', subscription: fakeSubscription() },
  {
    label: 'Standard',
    note: 'unlimited + tasks/bulk',
    subscription: fakeSubscription({ hasBulk: true, hasTasks: () => true }),
  },
  {
    label: 'Pro',
    note: 'unlimited + offline socket',
    subscription: fakeSubscription({ hasBulk: true, hasTasks: () => true, hasSocket: () => true }),
  },
];

export default function TestComponentsView() {
  const dispatch = useAppDispatch();
  const [editorState, setEditorState] = useState<ScheduledMessageEditorState | null>(null);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const shadowDOMContainerRef = useRef<HTMLDivElement>(null);

  const handleEditorStateChange = (state: ScheduledMessageEditorState) => {
    setEditorState(state);
  };

  const handleOpenPopup = () => {
    dispatch(addScheduledMessageButtonActions.editMessage({
      message: 'Test Message',
      state: MessageState.pending,
      dueDate: new Date().toISOString(),
      isRecurring: false,
      cancelIfReceived: false
    } as any));
  };

  useEffect(() => {
    if (shadowDOMContainerRef.current) {
      injectShadowDOMTextSelection(shadowDOMContainerRef.current).catch((error) => {
        console.error('Failed to inject Shadow DOM component:', error);
      });
    }
  }, []);

  return (
    <div className="min-h-screen p-8" style={{ background: 'var(--color-bg)', color: 'var(--color-text)' }}>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Test Components</h1>

        {/* Floating LTR/RTL switch — most of our layout bugs only show up in RTL. */}
        <DirectionToggle />

        <div className="space-y-8">
          {/* Plan badges — every tier of the header SubscriptionStatusBar side by side */}
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm border border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-semibold mb-4">{PLAN_SECTION.title}</h2>
            <p className="text-sm text-slate-500 mb-4">{PLAN_SECTION.description}</p>
            {/* The same row twice: as-is, and under a forced `.dark` ancestor so both
                themes can be compared without flipping the app theme. */}
            {[false, true].map((dark) => (
              <div
                key={dark ? 'dark' : 'light'}
                className={`flex flex-wrap gap-6 rounded-lg p-4 ${dark ? 'dark mt-3' : ''}`}
                style={{ background: dark ? '#111219' : 'transparent' }}
              >
                {PLAN_PREVIEWS.map(({ label, note, subscription }) => (
                  <div key={label} className="flex flex-col items-start gap-2">
                    <SubscriptionStatusBar compact previewSubscription={subscription} onUpgradeClick={() => {}} />
                    <div className={`text-xs ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                      <div className="font-medium">{label}</div>
                      <div>{note}</div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Welcome / Onboarding Popup Test */}
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm border border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-semibold mb-4">Welcome (Onboarding) Popup</h2>
            <p className="text-sm text-slate-500 mb-4">
              This is the popup shown to users right after they register. In production it only
              triggers once (via the <code>?welcome=true</code> param). Use this button to re-trigger it for testing.
            </p>
            <button
              onClick={() => setShowWelcomePopup(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Open Welcome Popup
            </button>
            <WelcomePopup
              isOpen={showWelcomePopup}
              onClose={() => setShowWelcomePopup(false)}
            />
          </div>

           {/* Scheduler Popup Test */}
           <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm border border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-semibold mb-4">Scheduler Popup</h2>
            <button 
              onClick={handleOpenPopup}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Open Scheduler Popup
            </button>
            <ScheduledMessageEditorPopup />
          </div>

          {/* Contact Table Test - Import contacts component */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col" style={{ height: '500px' }}>
            <ContactTable />
          </div>

          {/* Scheduled Message Editor Wrapper Test */}
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm border border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-semibold mb-4">Scheduled Message Editor Wrapper</h2>
            <div className="space-y-4">
              {editorState && (
                <div>
                  <label className="block text-sm font-medium mb-2">Current State:</label>
                  <div className="text-sm font-mono bg-slate-100 dark:bg-slate-900 p-4 rounded max-h-64 overflow-y-auto">
                    <pre>{JSON.stringify(editorState, null, 2)}</pre>
                  </div>
                </div>
              )}
              <div>
                <ScheduledMessageEditorWrapper
                  incomingMessage={editorState?.message || ''}
                  incomingIsPoll={editorState?.isPoll || false}
                  incomingPoll={editorState?.poll || {}}
                  incomingAsset={editorState?.asset || { name: '', type: '', url: '' }}
                  incomingImageId={editorState?.imageId || ''}
                  incomingSendLinkPreview={editorState?.sendLinkPreview || false}
                  incomingLinkPreview={editorState?.linkPreview || {}}
                  incomingTemplateId={editorState?.templateId || ''}
                  incomingDueDate={editorState?.dueDate || ''}
                  incomingRruleset={editorState?.rruleset || ''}
                  incomingIsRecurring={editorState?.isRecurring || false}
                  incomingCancelIfReceived={editorState?.cancelIfReceived || false}
                  incomingRetryLimitDuration={editorState?.retryLimitDuration}
                  incomingRetryLimitUnit={editorState?.retryLimitUnit}
                  onStateChange={handleEditorStateChange}
                  isPreview={false}
                  editorContext="scheduleMessage"
                />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

